import { Button, Stack, Typography } from '@mui/material'
import { Icons } from 'utoniq-ui'

import { BaseLayout, Head } from '@/components/layouts'
import { DeactivationBox } from '@/components/organisms'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { getPlatform } from '@/core/native/app'
import { filterHiddenItem } from '@/lib/array'

export * from './fragments'

export type State = {}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {}

export const DeactivationTemplate: Template<State, Action> = () => {
  const { routerNames, routerPages } = useRouter()
  const { t } = useTranslation('deactivation')

  return (
    <>
      <Head title={routerNames.Deactivation} />
      <BaseLayout
        margin
        header={{
          back: true,
          defaultBackPage: routerPages.AccountSetting,
          title: t('title'),
        }}
      >
        <DeactivationFirstNotesCell />
        <DeactivationSecondNotesCell />
        <DiactivationButton />
      </BaseLayout>
    </>
  )
}

const DeactivationFirstNotesCell: Cell = () => {
  const { t } = useTranslation('deactivation')

  const list = [
    { title: t('dataErrorTitle'), description: t('dataErrorDescription') },
    {
      title: t('revertDataErrorTitle'),
      description: t('revertDataErrorDescription'),
    },
  ]

  return (
    <CellFrame sx={{ pt: 3, pb: 6 }}>
      <Typography variant="body2">{t('note')}</Typography>
      <Stack spacing={4} sx={{ pt: 4 }}>
        {list.map((item, index) => (
          <DeactivationBox
            key={index}
            type="error"
            Icon={Icons.Error}
            title={item.title}
            description={item.description}
          />
        ))}
      </Stack>
    </CellFrame>
  )
}

const DeactivationSecondNotesCell: Cell = () => {
  const { t } = useTranslation('deactivation')
  const { routerPages, generatePath } = useRouter()
  const isStandalone = config.applicationType === 'standalone'
  const list = [
    {
      Icon: Icons.Notice,
      title: t('notificationTitle'),
      description: t('notificationDescription'),
      actions:
        getPlatform() !== 'web'
          ? [
              {
                children: t('changePushNotification'),
                href: routerPages.PushNotificationSettings,
              },
              {
                children: t('changeEmailNotification'),
                href: routerPages.EmailNotificationSettings,
              },
            ]
          : [
              {
                children: t('changeEmailNotification'),
                href: routerPages.EmailNotificationSettings,
              },
            ],
    },
    {
      Icon: Icons.Thread,
      title: t('threadTitle'),
      hidden: !isStandalone || !config.modules.thread,
      description: t('threadDescription'),
      actions: [
        {
          children: t('useCommunity'),
          href:
            config.artistId &&
            generatePath(routerPages.ArtistThreads, {
              artistId: config.artistId,
            }),
        },
      ],
    },
    {
      Icon: Icons.Market,
      title: t('tradeTitle'),
      hidden: !isStandalone || !config.modules.trade,
      description: t('tradeDescription'),
      actions: [
        {
          children: t('takeAdvantageTrading'),
          href:
            config.artistId &&
            generatePath(routerPages.ArtistTrade, {
              artistId: config.artistId,
            }),
        },
      ],
    },
  ]
  return (
    <CellFrame sx={{ pb: 6 }}>
      <Typography variant="h5" sx={{ py: 2 }}>
        {t('beforeWithdrawing')}
      </Typography>
      <Stack spacing={4}>
        {filterHiddenItem(list).map((item, index) => (
          <DeactivationBox {...item} key={index} />
        ))}
      </Stack>
    </CellFrame>
  )
}

type DiactivationButtonProps = {}
const DiactivationButton: Cell<DiactivationButtonProps> = () => {
  const { routerPages } = useRouter()
  const { t } = useTranslation('deactivation')
  return (
    <CellFrame sx={{ py: 4, textAlign: 'center' }}>
      <Button
        size="large"
        sx={{ minWidth: 244 }}
        href={routerPages.DeactivationConfirm}
      >
        {t('proceedWithdrawalProcedure')}
      </Button>
    </CellFrame>
  )
}
